import React, { useEffect, useRef, useState } from 'react';

// Renders a 2D icon that *looks* 3D: a sphere whose dark/light hemispheres
// rotate around the Y axis. We don't use rotateY on the SVG — that flips it
// like a card. Instead we redraw the visible filled region every frame as
// the projected sphere turns: the boundary between halves is an ellipse
// whose horizontal radius is R*|sin(theta)|, and which side of that ellipse
// is "filled" depends on the quadrant of theta.
const TAU = Math.PI * 2;
const DURATION_MS = 600;

// Build the SVG path for the filled hemisphere visible at angle theta.
// At theta=0 the right half is filled (matches MUI Contrast).
function filledPath(theta, R) {
  let t = theta % TAU;
  if (t < 0) t += TAU;

  const absS = Math.abs(Math.sin(t)) * R;

  // Sweep flags per quadrant: (discArc, ellipseArc)
  // Q1 (0..pi/2):       right lune        (1, 0)
  // Q2 (pi/2..pi):      left lune         (0, 1)
  // Q3 (pi..3pi/2):     disc - right lune (0, 0)
  // Q4 (3pi/2..2pi):    disc - left lune  (1, 1)
  let discSweep, ellipseSweep;
  if (t < Math.PI / 2)         { discSweep = 1; ellipseSweep = 0; }
  else if (t < Math.PI)        { discSweep = 0; ellipseSweep = 1; }
  else if (t < 3 * Math.PI/2)  { discSweep = 0; ellipseSweep = 0; }
  else                         { discSweep = 1; ellipseSweep = 1; }

  return `M 0 ${-R} `
       + `A ${R} ${R} 0 0 ${discSweep} 0 ${R} `
       + `A ${absS} ${R} 0 0 ${ellipseSweep} 0 ${-R} Z`;
}

function ease(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // rotation accelerates/decelerates
  //return t; // change to this for linear rotation
}

// `spinKey` is any value (we use a counter). Each time it changes, the
// sphere adds another 180° to its target rotation in the same direction.
const SpinSphereIcon = ({ size = 24, spinKey = 0, ...rest }) => {
  const [theta, setTheta] = useState(0);
  const targetRef = useRef(0);
  const startThetaRef = useRef(0);
  const startTimeRef = useRef(0);
  const rafRef = useRef(null);
  const lastKeyRef = useRef(spinKey);

  useEffect(() => {
    if (spinKey === lastKeyRef.current) return;
    lastKeyRef.current = spinKey;

    targetRef.current += Math.PI; // modify this to change how many spins per click
    startThetaRef.current = theta;
    startTimeRef.current = performance.now();

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const tick = (now) => {
      const p = Math.min(1, (now - startTimeRef.current) / DURATION_MS);
      const next = startThetaRef.current
                 + (targetRef.current - startThetaRef.current) * ease(p);
      setTheta(next);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinKey]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const R = 9;
  const d = filledPath(theta, R);

  // Fixed colors so the visual swap is real: the sphere is its own
  // self-contained black/white object, independent of the theme. The outer
  // ring still uses currentColor so the silhouette adapts to the theme.
  const DARK = '#1a1714';
  const LIGHT = '#f7f4ef';

  return (
    <svg
      width={size}
      height={size}
      viewBox="-12 -12 24 24"
      style={{ display: 'block' }}
      aria-hidden="true"
      {...rest}
    >
      <circle cx="0" cy="0" r={R} fill={DARK} />
      <path d={d} fill={LIGHT} />
      <circle cx="0" cy="0" r={R} fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
};

export default SpinSphereIcon;
